/**
 * /admin/email-templates — edit the global default templates used by
 * Pulse's transactional outbound emails. Session 88 — currently just
 * the proposal follow-up template (subject + body_html + cadence).
 *
 * The body editor is the same `RichTextEditor` (TipTap) used in
 * marketing workflows, so fonts/colours/links/signatures all work the
 * same way. The rendered email is wrapped in `eyvo_email_template`
 * server-side at send time so it looks identical to every other
 * transactional email Pulse fires.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/layout/AppLayout";
import RichTextEditor from "@/pages/marketing/RichTextEditor";

export default function EmailTemplatesPage() {
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [reminderDays, setReminderDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/quote-followup-template");
      setSubject(r.data.subject || "");
      setBodyHtml(r.data.body_html || "");
      setReminderDays(r.data.reminder_days || 7);
      setUpdatedAt(r.data.updated_at || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load template");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!subject.trim()) { toast.error("Subject can't be empty"); return; }
    if (!bodyHtml.trim()) { toast.error("Body can't be empty"); return; }
    if (reminderDays < 1 || reminderDays > 90) { toast.error("Reminder days must be 1–90"); return; }
    setSaving(true);
    try {
      await api.put("/admin/quote-followup-template", {
        subject: subject.trim(),
        body_html: bodyHtml,
        reminder_days: Number(reminderDays),
      });
      toast.success("Template saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-8 max-w-4xl" data-testid="email-templates-page">
        <h1 className="text-2xl font-bold text-[#0A0A0A] mb-1">Email templates</h1>
        <p className="text-sm text-[#737373] mb-6">
          Global defaults for system-generated outbound emails. Operators
          can still edit each email before sending — these define the
          starting content.
        </p>

        {loading ? (
          <div className="text-sm text-[#A3A3A3]">Loading…</div>
        ) : (
          <div className="border border-[#E5E5E5] rounded-md p-6 bg-white">
            <h2 className="text-lg font-bold text-[#0A0A0A] mb-1">Proposal follow-up</h2>
            <p className="text-xs text-[#737373] mb-4">
              Default content for the <em>Send Follow-up</em> button on
              each proposal. Supported placeholders: <code>{"{quote_number}"}</code>,
              {" "}<code>{"{first_name}"}</code>, <code>{"{sent_date}"}</code>,
              {" "}<code>{"{sender_name}"}</code>, <code>{"{client_name}"}</code>.
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                  data-testid="email-tmpl-subject"
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#525252] block mb-1">Body</label>
                <RichTextEditor
                  html={bodyHtml}
                  onChange={setBodyHtml}
                  placeholder="Write the default follow-up body. Use placeholders like {first_name} which will be replaced at send time."
                  minHeight={320}
                />
              </div>

              <div className="border-t border-[#F5F5F5] pt-4">
                <h3 className="text-sm font-bold text-[#0A0A0A] mb-1">Internal reminder cadence</h3>
                <p className="text-xs text-[#737373] mb-2">
                  How many days between automatic nudges to the sales rep
                  while a proposal sits with no response.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={reminderDays}
                    onChange={(e) => setReminderDays(e.target.value)}
                    className="w-20 border border-[#E5E5E5] px-3 py-2 text-sm text-right focus:outline-none focus:border-[#0055FF]"
                    data-testid="email-tmpl-reminder-days"
                  />
                  <span className="text-sm text-[#525252]">days</span>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-[#F5F5F5]">
                <div className="text-[10px] text-[#A3A3A3]">
                  {updatedAt ? `Last updated ${new Date(updatedAt).toLocaleString()}` : ""}
                </div>
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-bold bg-[#0055FF] text-white uppercase tracking-wider hover:bg-[#0044CC] disabled:opacity-50"
                  data-testid="email-tmpl-save"
                >
                  {saving ? "Saving…" : "Save template"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
